import {
  Injectable,
  Logger,
  OnApplicationShutdown,
  OnModuleInit,
} from '@nestjs/common';
import { PrismaClient } from '@prisma/client';

type AuditTable = 'interfaceLog' | 'activityLog' | 'changeLog' | 'changeBatch';

interface QueuedRow {
  table: AuditTable;
  data: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 1000;
const FLUSH_AT_ROWS = 200;
/**
 * Hard ceiling on rows held in memory. Past this, rows are dropped rather than
 * queued. An audit trail is allowed to lose a sampled diagnostic row under
 * extreme load; it is not allowed to become the outage by exhausting heap
 * behind a slow database.
 */
const MAX_BUFFERED_ROWS = 5000;

/**
 * Batches every audit write and flushes it off the request path.
 *
 * Two things this fixes about the previous mechanism:
 *
 * 1. It wrote one row per request, individually, inline. Now rows accumulate
 *    and go out via createMany on a timer.
 * 2. It shared the application's connection pool, so an audit insert competed
 *    with a checkout for a connection. This writer holds its own PrismaClient
 *    with a small connection_limit, so audit traffic cannot starve user
 *    traffic no matter how far behind it falls.
 */
@Injectable()
export class AuditWriterService implements OnModuleInit, OnApplicationShutdown {
  private readonly logger = new Logger(AuditWriterService.name);
  private readonly buffer: QueuedRow[] = [];
  private timer?: NodeJS.Timeout;
  private flushing = false;
  private client?: PrismaClient;

  /** Dropped-row counter — without it, shedding load would be silent. */
  private dropped = 0;
  private lastDropWarnAt = 0;

  onModuleInit(): void {
    this.timer = setInterval(() => {
      void this.flush();
    }, FLUSH_INTERVAL_MS);

    // Never keep the process alive just to flush audit rows.
    this.timer.unref?.();
  }

  async onApplicationShutdown(): Promise<void> {
    if (this.timer) clearInterval(this.timer);

    // A deploy would otherwise silently eat whatever is still buffered.
    await this.flush();
    await this.client?.$disconnect();
  }

  enqueue(table: AuditTable, data: Record<string, unknown>): void {
    if (this.buffer.length >= MAX_BUFFERED_ROWS) {
      this.dropped += 1;
      this.warnAboutDrops();
      return;
    }

    this.buffer.push({ table, data });

    if (this.buffer.length >= FLUSH_AT_ROWS) void this.flush();
  }

  droppedCount(): number {
    return this.dropped;
  }

  bufferedCount(): number {
    return this.buffer.length;
  }

  async flush(): Promise<void> {
    if (this.flushing || this.buffer.length === 0) return;

    this.flushing = true;
    const batch = this.buffer.splice(0, this.buffer.length);

    try {
      const grouped = new Map<AuditTable, Record<string, unknown>[]>();
      for (const row of batch) {
        const rows = grouped.get(row.table) ?? [];
        rows.push(row.data);
        grouped.set(row.table, rows);
      }

      const client = this.resolveClient();

      for (const [table, rows] of grouped) {
        // skipDuplicates: a retried batch or a re-sent activity beacon must not
        // abort the whole flush on one primary-key collision.
        // Typed as unknown deliberately: Prisma.InputJsonValue is a deeply
        // recursive union, and asserting an array of plain records against it
        // sends tsc into an assignability walk that overflows its stack.
        const delegate = client[table] as unknown as {
          createMany: (args: { data: unknown[]; skipDuplicates: boolean }) => Promise<unknown>;
        };

        await delegate.createMany({ data: rows, skipDuplicates: true });
      }
    } catch (error) {
      // Deliberately swallowed: audit failure must never surface as request
      // failure. The rows are gone, but the warning says so rather than
      // failing silently the way the previous fire-and-forget writes did.
      this.logger.warn(
        `audit flush failed, dropped ${batch.length} row(s): ${this.errorMessage(error)}`
      );
      this.dropped += batch.length;
    } finally {
      this.flushing = false;
    }
  }

  /**
   * A second client on its own small pool. Falls back to a shared default if
   * the URL is unavailable, so a misconfiguration degrades to "audit shares the
   * pool" rather than "audit throws on boot".
   */
  private resolveClient(): PrismaClient {
    if (this.client) return this.client;

    const url = process.env.AUDIT_DATABASE_URL ?? this.poolLimitedUrl();
    this.client = url
      ? new PrismaClient({ datasources: { db: { url } } })
      : new PrismaClient();

    return this.client;
  }

  private poolLimitedUrl(): string | undefined {
    const base = process.env.DATABASE_URL;
    if (!base) return undefined;

    try {
      const url = new URL(base);
      const limit = process.env.AUDIT_DB_POOL_SIZE ?? '3';
      url.searchParams.set('connection_limit', limit);
      url.searchParams.set('pool_timeout', '5');
      return url.toString();
    } catch {
      return undefined;
    }
  }

  private warnAboutDrops(): void {
    const now = Date.now();
    if (now - this.lastDropWarnAt < 30_000) return;

    this.lastDropWarnAt = now;
    this.logger.warn(
      `audit buffer full (${MAX_BUFFERED_ROWS}); ${this.dropped} row(s) dropped so far`
    );
  }

  private errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }
}
